// src/pages/Users/Galleries/AddPhotoToAlbumModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  FaCheck,
  FaCloudUploadAlt,
  FaEye,
  FaEyeSlash,
  FaImage,
  FaSave,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  addPhoto,
  updatePhoto,
  deletePhoto,
} from "../../../services/photoServices";

import {
  replaceAlbumPhoto,
  toAbsolutePhotoUrl as toAbsPhoto,
} from "../../../services/albumPhotoServices";

import { getGalleryById } from "../../../services/gallerieServices";

const FALLBACK = "/img/no-image.jpg";

export default function AddPhotoToAlbumModal({
  isOpen,
  onClose,
  galleryId,
  photoId,
}) {
  const [currentId, setCurrentId] = useState(photoId ?? null);
  const isEdit = Boolean(currentId);

  const [photoUrl, setPhotoUrl] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [position, setPosition] = useState("");
  const [isCover, setIsCover] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState("");

  const fileInputRef = useRef(null);
  const descriptionRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Refs pour éviter que le useEffect du clavier/focus se relance à chaque saisie
  const stateRef = useRef({
    currentId: null,
    photoUrl: "",
    description: "",
    selectedFile: null,
  });

  useEffect(() => {
    stateRef.current = {
      currentId,
      photoUrl,
      description,
      selectedFile,
    };
  }, [currentId, photoUrl, description, selectedFile]);

  useEffect(() => {
    setCurrentId(photoId ?? null);
  }, [photoId]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewObjectUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewObjectUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const previewURL = useMemo(() => {
    if (previewObjectUrl) return previewObjectUrl;
    return toAbsPhoto(photoUrl) || "";
  }, [previewObjectUrl, photoUrl]);

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  const handleClose = useCallback(async () => {
    const latest = stateRef.current;

    const noImage = !latest.photoUrl && !latest.selectedFile;
    const noDesc = !String(latest.description || "").trim();

    if (latest.currentId && noImage && noDesc) {
      const ok = window.confirm("Annuler et supprimer l’entrée vide ?");

      if (ok) {
        try {
          await deletePhoto(latest.currentId);
          toast.success("Photo supprimée.");
        } catch {
          toast.error("Échec de la suppression.");
        }
      }
    }

    onClose?.();
  }, [onClose]);

  // Lock scroll + ESC + focus une seule fois à l’ouverture
  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", onKey);

    const timer = setTimeout(() => {
      descriptionRef.current?.focus();
    }, 100);

    return () => {
      window.removeEventListener("keydown", onKey);
      root.style.overflow = previousOverflow;
      clearTimeout(timer);
    };
  }, [isOpen, handleClose]);

  // Reset léger à l’ouverture
  useEffect(() => {
    if (!isOpen) return;

    setCurrentId(photoId ?? null);
    setPhotoUrl("");
    setDescription("");
    setIsActive(true);
    setPosition("");
    setIsCover(false);
    setSelectedFile(null);
    setPreviewObjectUrl("");
    setSaving(false);
    setUploading(false);
    setDragActive(false);
  }, [isOpen, photoId]);

  // Pré-remplir la position avec max(position)+1
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) return;

    (async () => {
      try {
        setLoadingDefaults(true);

        const gal = await getGalleryById(Number(galleryId));
        const photos = Array.isArray(gal?.photos) ? gal.photos : [];

        const maxPos = photos.reduce((max, photo) => {
          const pos = Number(photo?.position);
          return Number.isFinite(pos) ? Math.max(max, pos) : max;
        }, 0);

        setPosition(String(maxPos + 1));
      } catch {
        setPosition("");
      } finally {
        setLoadingDefaults(false);
      }
    })();
  }, [isOpen, isEdit, galleryId]);

  const chooseFile = (file) => {
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      toast.warn("Choisis une image valide (JPG, PNG, WebP…).");
      return;
    }

    setSelectedFile(file);
  };

  const onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    chooseFile(event.dataTransfer.files?.[0]);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  // Même payload que ton ancien code
  const buildPayload = (forCreate = false) => {
    const payload = {
      gallerie_id: galleryId,
      description,
      is_active: isActive,
    };

    if (
      String(position).trim() !== "" &&
      !Number.isNaN(parseInt(position, 10))
    ) {
      payload.position = parseInt(position, 10);
    }

    if (forCreate) {
      payload.is_cover = !!isCover;
    }

    return payload;
  };

  // Même logique que ton ancien code
  const handleUploadImage = async () => {
    if (!selectedFile) {
      toast.warn("Choisis une image avant de téléverser.");
      return;
    }

    try {
      setUploading(true);

      if (!currentId) {
        const created = await addPhoto(buildPayload(true));

        setCurrentId(created?.id || null);
        setPhotoUrl(created?.photo_url || "");

        if (created?.id) {
          const updated = await replaceAlbumPhoto(
            galleryId,
            created.id,
            selectedFile,
            { format: "webp" }
          );

          setPhotoUrl(updated?.photo_url || created?.photo_url || "");
        }

        setSelectedFile(null);
        toast.success("Image téléversée sur le serveur.");
        return;
      }

      const updated = await replaceAlbumPhoto(
        galleryId,
        currentId,
        selectedFile,
        { format: "webp" }
      );

      setPhotoUrl(updated?.photo_url || "");
      setSelectedFile(null);
      toast.success("Image remplacée sur le serveur.");
    } catch (error) {
      console.error(error);
      toast.error("Échec du téléversement de l’image.");
    } finally {
      setUploading(false);
    }
  };

  // Même logique que ton ancien code
  const handleSaveInfos = async () => {
    if (!currentId) {
      try {
        setSaving(true);

        const created = await addPhoto(buildPayload(true));

        setCurrentId(created?.id || null);
        setPhotoUrl(created?.photo_url || "");

        toast.success("Infos enregistrées. Téléverse l’image pour finaliser.");
      } catch (error) {
        console.error(error);
        toast.error("Échec de la création de la photo.");
      } finally {
        setSaving(false);
      }

      return;
    }

    try {
      setSaving(true);

      await updatePhoto(currentId, buildPayload(false));

      toast.success("Infos mises à jour.");
    } catch (error) {
      console.error(error);
      toast.error("Échec de la sauvegarde des infos.");
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = () => {
    onClose?.();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/70 p-3 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Éditer une photo" : "Ajouter une photo"}
      onClick={(event) => {
        if (event.currentTarget === event.target) {
          handleClose();
        }
      }}
    >
      <article
        className="absolute inset-x-0 bottom-0 flex max-h-[94dvh] flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 sm:relative sm:inset-auto sm:w-full sm:max-w-4xl sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="relative overflow-hidden bg-gradient-to-r from-slate-950 via-blue-900 to-violet-800 px-5 py-5 text-white sm:px-6">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/15 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ring-1 ring-white/20">
                <FaImage />
                {isEdit ? "Modification photo" : "Nouvelle photo"}
              </span>

              <h2 className="mt-3 text-2xl font-bold leading-tight">
                {isEdit ? "Éditer la photo" : "Ajouter une photo"}
              </h2>

              <p className="mt-1 text-sm leading-6 text-white/80">
                Téléverse une image, ajoute une légende et règle sa position
                dans l’album.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25 active:scale-95"
              aria-label="Fermer"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
            <section>
              <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`overflow-hidden rounded-[1.5rem] border bg-slate-50 transition dark:bg-slate-950 ${
                  dragActive
                    ? "border-sky-400 ring-4 ring-sky-400/10"
                    : "border-slate-200 dark:border-slate-800"
                }`}
              >
                <div className="aspect-[16/10] w-full bg-slate-100 dark:bg-slate-900">
                  <img
                    src={previewURL || FALLBACK}
                    alt="Aperçu"
                    className="h-full w-full object-contain"
                    onError={onImgError}
                  />
                </div>

                <div className="border-t border-slate-200 p-4 dark:border-slate-800">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-950 dark:text-white">
                        Image de l’album
                      </p>
                      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                        Dépose une image ou sélectionne un fichier.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) =>
                          chooseFile(event.target.files?.[0])
                        }
                      />

                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
                      >
                        <FaCloudUploadAlt />
                        Choisir
                      </button>

                      <button
                        type="button"
                        onClick={handleUploadImage}
                        disabled={uploading || !selectedFile}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950"
                      >
                        <FaUpload />
                        {uploading ? "Téléversement..." : "Téléverser"}
                      </button>
                    </div>
                  </div>

                  {selectedFile && (
                    <div className="mt-3 rounded-2xl bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 dark:bg-sky-950/30 dark:text-sky-300">
                      Image sélectionnée : {selectedFile.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  URL de l’image serveur
                </p>

                <input
                  type="text"
                  value={toAbsPhoto(photoUrl)}
                  readOnly
                  placeholder="Visible après téléversement"
                  className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                />

                <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                  Cette URL est générée automatiquement après le téléversement.
                </p>
              </div>
            </section>

            <section className="space-y-5">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  Légende / description
                </label>

                <textarea
                  ref={descriptionRef}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={5}
                  placeholder="Ex. Podium Minimes — Merci aux bénévoles !"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />

                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  Cette légende pourra être affichée sous la photo.
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  Position dans l’album
                </label>

                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={position}
                  onChange={(event) => setPosition(event.target.value)}
                  placeholder={
                    loadingDefaults
                      ? "Chargement..."
                      : "Laisser vide = position suivante"
                  }
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />

                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  Laisse vide pour laisser le système choisir la prochaine
                  position.
                </p>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-slate-950 dark:text-white">
                      Visibilité de la photo
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                      Une photo visible apparaît dans l’album. Une photo
                      masquée reste disponible côté gestion.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setIsActive((value) => !value)}
                    className={`relative inline-flex h-8 w-[62px] shrink-0 items-center rounded-full p-1 transition ${
                      isActive
                        ? "bg-emerald-500"
                        : "bg-slate-300 dark:bg-slate-700"
                    }`}
                    aria-pressed={isActive}
                    aria-label="Basculer la visibilité"
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full bg-white text-[11px] shadow transition ${
                        isActive ? "translate-x-[30px]" : "translate-x-0"
                      }`}
                    >
                      {isActive ? (
                        <FaEye className="text-emerald-600" />
                      ) : (
                        <FaEyeSlash className="text-slate-500" />
                      )}
                    </span>
                  </button>
                </div>

                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                  {isActive ? (
                    <>
                      <FaCheck className="text-emerald-500" />
                      Photo visible
                    </>
                  ) : (
                    <>
                      <FaEyeSlash className="text-slate-400" />
                      Photo masquée
                    </>
                  )}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-sky-100 bg-sky-50 p-4 text-xs leading-6 text-sky-800 dark:border-sky-900/40 dark:bg-sky-950/20 dark:text-sky-200">
                <p className="font-bold">Astuce</p>
                <p>
                  Si tu veux que la position soit respectée, clique sur
                  “Téléverser” : le système crée d’abord l’entrée photo,
                  puis associe l’image au bon ID.
                </p>
              </div>
            </section>
          </div>
        </div>

        <footer className="border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
          <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-end">
            <button
              type="button"
              onClick={handleSaveInfos}
              disabled={saving}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 text-sm font-bold text-slate-700 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaSave />
              {saving ? "Sauvegarde..." : "Infos"}
            </button>

            <button
              type="button"
              onClick={handleFinish}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
            >
              <FaTimes />
              Terminer
            </button>
          </div>

          <p className="mt-3 text-center text-[11px] text-slate-500 dark:text-slate-400 sm:text-right">
            {selectedFile
              ? "Une image est sélectionnée : clique sur “Téléverser” pour l’envoyer au serveur."
              : currentId
              ? "L’entrée photo existe. Tu peux modifier les infos ou remplacer l’image."
              : "Tu peux enregistrer les infos ou téléverser directement l’image."}
          </p>
        </footer>
      </article>
    </div>,
    document.body
  );
}